import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useUserSettings } from '../../shared/context/UserSettingsContext';
import { API_BASE } from '../../shared/Constants';

const MAX_NAME = 120;
const MAX_INSTRUCTIONS = 4000;

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

const Container = styled.div`
  background: ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
  border: 1px solid ${({ theme }) => theme.user_menu_border || theme.sidebar_border};
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 640px;
  max-height: 85vh;
  overflow-y: auto;
  padding: 24px;
  color: ${({ theme }) => theme.font_color};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h2`
  font-size: 20px;
  margin: 0;
  color: ${({ theme }) => theme.font_color};
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  display: flex;
`;

const Label = styled.label`
  display: block;
  font-size: 15px;
  font-weight: 600;
  margin: 16px 0 6px;
  color: ${({ theme }) => theme.font_color};
`;

const Hint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.user_menu_border || theme.sidebar_border};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 15px;
  &:focus { border-color: ${({ theme }) => theme.prompt_accent_color}; outline: none; }
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 120px;
  max-height: 300px;
  resize: none;
  overflow-y: auto;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.user_menu_border || theme.sidebar_border};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-family: inherit;
  font-size: 15px;
  &:focus { border-color: ${({ theme }) => theme.prompt_accent_color}; outline: none; }
`;

const Counter = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text_secondary};
  text-align: right;
  margin-top: 4px;
`;

const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.user_menu_border || theme.sidebar_border};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 15px;
  &:focus { border-color: ${({ theme }) => theme.prompt_accent_color}; outline: none; }
  option {
    background: ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
    color: ${({ theme }) => theme.font_color};
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
`;

const PrimaryButton = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  background: #15803d;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover:not(:disabled) { background: #166534; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

interface Props {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
}

const UserSettingsModal: React.FC<Props> = ({ open, onClose, onBack }) => {
  const { displayName, defaultInstructions, ttsVoice, save, saving } = useUserSettings();
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [ttsVoiceChoice, setTtsVoiceChoice] = useState('');
  const [ttsVoiceOptions, setTtsVoiceOptions] = useState<Array<{ voice_id: string; label: string }>>([]);

  // Reset local form state from context whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setName(displayName || '');
      setInstructions(defaultInstructions || '');
      setTtsVoiceChoice(ttsVoice || '');
    }
  }, [open, displayName, defaultInstructions, ttsVoice]);

  // Catálogo de vozes (o backend é a fonte; a primeira é a padrão).
  useEffect(() => {
    if (!open || ttsVoiceOptions.length > 0) return;
    fetch(`${API_BASE}/api/tts/voices`)
      .then((res) => res.json())
      .then((data) => setTtsVoiceOptions(data?.voices || []))
      .catch(() => {});
  }, [open, ttsVoiceOptions.length]);

  const handleSave = async () => {
    await save({
      display_name: name.trim(),
      default_instructions: instructions,
      tts_voice: ttsVoiceChoice,
    });
    onClose();
  };

  return (
    <Overlay $open={open} onClick={onClose}>
      <Container onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Personalização">
        <Header>
          <HeaderLeft>
            <IconButton onClick={onBack || onClose} aria-label="Voltar"><ArrowBackIcon /></IconButton>
            <Title>Personalização</Title>
          </HeaderLeft>
          <IconButton onClick={onClose} aria-label="Fechar"><CloseIcon /></IconButton>
        </Header>

        <Label htmlFor="settings-display-name">Nome de exibição</Label>
        <Hint>Como você quer ser chamado. Se vazio, usamos seu e-mail.</Hint>
        <Input
          id="settings-display-name"
          data-testid="display-name-input"
          value={name}
          maxLength={MAX_NAME}
          placeholder="Seu nome"
          onChange={(e) => setName(e.target.value.slice(0, MAX_NAME))}
        />

        <Label htmlFor="settings-default-instructions">Instruções padrão</Label>
        <Hint>Aplicadas a todas as conversas (ex.: "Responda sempre em português, de forma concisa").</Hint>
        <TextArea
          id="settings-default-instructions"
          data-testid="default-instructions-input"
          value={instructions}
          maxLength={MAX_INSTRUCTIONS}
          placeholder="Instruções que o assistente deve seguir em todas as conversas..."
          onChange={(e) => setInstructions(e.target.value.slice(0, MAX_INSTRUCTIONS))}
        />
        <Counter>{instructions.length}/{MAX_INSTRUCTIONS}</Counter>

        <Label htmlFor="settings-tts-voice">Voz de leitura</Label>
        <Hint>Voz usada ao ouvir as mensagens em voz alta.</Hint>
        <Select
          id="settings-tts-voice"
          data-testid="tts-voice-select"
          value={ttsVoiceChoice || (ttsVoiceOptions[0]?.voice_id ?? '')}
          onChange={(e) => setTtsVoiceChoice(e.target.value)}
        >
          {ttsVoiceOptions.map((opt) => (
            <option key={opt.voice_id} value={opt.voice_id}>{opt.label}</option>
          ))}
        </Select>

        <Footer>
          <PrimaryButton data-testid="settings-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </PrimaryButton>
        </Footer>
      </Container>
    </Overlay>
  );
};

export default UserSettingsModal;
